import Phaser from 'phaser';
import { BootScene } from './scenes/BootScene';
import { PreloadScene } from './scenes/PreloadScene';
import { LobbyScene } from './scenes/LobbyScene';
import { GameScene } from './scenes/GameScene';
import { GameOverScene } from './scenes/GameOverScene';
import { TransitionOverlayScene } from './scenes/TransitionOverlayScene';
import CanvasInputPlugin from 'phaser3-rex-plugins/plugins/canvasinput-plugin.js';
import { SCALED_WIDTH, SCALED_HEIGHT, isIOS } from './renderScale';

// Re-export render scale utilities for convenience
export {
  RENDER_SCALE,
  SCALED_WIDTH,
  SCALED_HEIGHT,
  applyRenderScale,
  isIOS,
} from './renderScale';

// Storage key for forcing Canvas renderer after WebGL failure
const FORCE_CANVAS_KEY = 'forceCanvasRenderer';

/**
 * Determine which renderer to use.
 * Falls back to Canvas if WebGL has previously crashed.
 */
function getRendererType(): number {
  // Check if Canvas mode was forced due to previous crash
  if (localStorage.getItem(FORCE_CANVAS_KEY) === 'true') {
    console.log('[Hokm] Using Canvas renderer (WebGL previously failed)');
    return Phaser.CANVAS;
  }
  return Phaser.AUTO;
}

/**
 * Force Canvas renderer on next page load.
 * Call this when WebGL fails or crashes.
 */
export function forceCanvasRenderer(): void {
  localStorage.setItem(FORCE_CANVAS_KEY, 'true');
}

/**
 * Clear the Canvas renderer override.
 * Allows WebGL to be tried again.
 */
export function clearCanvasOverride(): void {
  localStorage.removeItem(FORCE_CANVAS_KEY);
}

/**
 * Check if Canvas mode is currently forced.
 */
export function isCanvasForced(): boolean {
  return localStorage.getItem(FORCE_CANVAS_KEY) === 'true';
}

export const gameConfig: Phaser.Types.Core.GameConfig = {
  type: getRendererType(),
  width: SCALED_WIDTH,
  height: SCALED_HEIGHT,
  parent: 'game-container',
  backgroundColor: '#1a4a3a', // Persian deep teal-green
  scale: {
    mode: Phaser.Scale.RESIZE,
    autoCenter: Phaser.Scale.CENTER_BOTH,
    // Render at device pixel ratio for crisp display on high-DPI screens
    zoom: window.devicePixelRatio || 1,
  },
  render: {
    pixelArt: false,
    // Disable antialiasing on iOS to reduce GPU memory usage
    antialias: !isIOS(),
    roundPixels: true,
    // Prefer low-power GPU on mobile for stability
    powerPreference: 'low-power',
  },
  dom: {
    createContainer: true,
  },
  plugins: {
    global: [
      {
        key: 'rexCanvasInputPlugin',
        plugin: CanvasInputPlugin,
        start: true,
      },
    ],
  },
  scene: [BootScene, PreloadScene, LobbyScene, GameScene, GameOverScene, TransitionOverlayScene],
};
